import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, ConfigProvider, theme as antdTheme, Checkbox, Table } from "antd";
import { getPerfilById, postPerfil } from "@/services/perfil";
import { getMenus } from "@/services/menu";

interface Menu {
  id: number;
  nome: string;
  subMenus?: Menu[];
}

interface Permissao {
  id?: number;
  visualizar: boolean;
  editar: boolean;
  inserir: boolean;
  menu: {
    id: number;
    nome: string;
  };
}

const defaultValues = {
  nome: "",
  ativo: true,
  permissoes: [] as Permissao[],
};

export default function CadastroPerfil() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [menus, setMenus] = useState<Menu[]>([]);
  const [permissoes, setPermissoes] = useState<Permissao[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  // Função para achatizar menus e submenus em uma lista única
  const flattenMenus = (menuList: Menu[]): Menu[] => {
    const flattened: Menu[] = [];
    
    menuList.forEach((menu) => {
      // Adiciona o menu principal
      flattened.push(menu);
      
      if (menu.subMenus && menu.subMenus.length > 0) {
        // Se tem submenus, adiciona os submenus
        menu.subMenus.forEach((subMenu) => {
          // Adiciona o submenu
          flattened.push(subMenu);
          
          if (subMenu.subMenus && subMenu.subMenus.length > 0) {
            // Se o submenu tem submenus, adiciona eles também
            subMenu.subMenus.forEach((subSubMenu) => {
              flattened.push(subSubMenu);
            });
          }
        });
      }
    });
    
    return flattened;
  };

  // Carregar menus do sistema
  useEffect(() => {
    const loadMenus = async () => {
      try {
        const response: any = await getMenus();
        if (response.status === 200) {
          const allMenus = response.data || [];
          setMenus(allMenus);
          
          // Achatizar menus e submenus
          const flattenedMenus = flattenMenus(allMenus);
          
          // Inicializar permissões para todos os menus achatizados
          const permissoesIniciais = flattenedMenus.map((menu: Menu) => ({
            visualizar: false,
            editar: false,
            inserir: false,
            menu: {
              id: menu.id,
              nome: menu.nome,
            },
          }));
          setPermissoes(permissoesIniciais);
        }
      } catch (error) {
        message.error("Erro ao carregar menus do sistema");
      }
    };

    loadMenus();
  }, []);

  // Carregar dados do perfil se for edição
  useEffect(() => {
    const loadPerfil = async () => {
      if (isEdit && id && menus.length > 0) {
        try {
          const response = await getPerfilById(id);
          if (response.status === 200) {
            const perfil = response.data;
            
            // Achatizar menus para comparar com permissões existentes
            const flattenedMenus = flattenMenus(menus);
            
            // Mapear permissões existentes
            const permissoesExistentes = flattenedMenus.map((menu: Menu) => {
              const permissaoExistente = perfil.permissoes?.find((p: any) => p.menu.id === menu.id);
              return {
                id: permissaoExistente?.id,
                visualizar: permissaoExistente?.visualizar || false,
                editar: permissaoExistente?.editar || false,
                inserir: permissaoExistente?.inserir || false,
                menu: {
                  id: menu.id,
                  nome: menu.nome,
                },
              };
            });
            
            setPermissoes(permissoesExistentes);
            
            reset({
              ...defaultValues,
              ...perfil,
              ativo: !!perfil.ativo,
              permissoes: permissoesExistentes,
            });
          }
        } catch (error) {
          message.error("Erro ao carregar dados do perfil");
        }
      } else if (!isEdit) {
        reset(defaultValues);
      }
    };

    if (menus.length > 0) {
      loadPerfil();
    }
  }, [id, isEdit, reset, menus]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        id: id || undefined,
        nome: data.nome.toUpperCase(),
        ativo: !!data.ativo,
        permissoes: permissoes.map(permissao => ({
          ...permissao,
          menuId: permissao.menu.id
        })),
      };
      
      const response = await postPerfil(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Perfil salvo com sucesso!");
        navigate("/perfil");
      }
    } catch (error: any) {
      message.error("Erro ao salvar perfil");
    }
  };

  // Atualizar permissão
  const updatePermissao = (menuId: number, field: 'visualizar' | 'editar' | 'inserir', value: boolean) => {
    setPermissoes(prev => 
      prev.map(p => 
        p.menu.id === menuId 
          ? { ...p, [field]: value }
          : p
      )
    );
  };

  // Colunas da tabela de permissões
  const columns = [
    {
      title: "Menu",
      dataIndex: "menu",
      key: "menu",
      render: (menu: any) => menu.nome,
    },
    {
      title: "Visualizar",
      key: "visualizar",
      render: (_: any, record: Permissao) => (
        <Checkbox
          checked={record.visualizar}
          onChange={(e) => updatePermissao(record.menu.id, 'visualizar', e.target.checked)}
        />
      ),
    },
    {
      title: "Editar",
      key: "editar",
      render: (_: any, record: Permissao) => (
        <Checkbox
          checked={record.editar}
          onChange={(e) => updatePermissao(record.menu.id, 'editar', e.target.checked)}
        />
      ),
    },
    {
      title: "Inserir",
      key: "inserir",
      render: (_: any, record: Permissao) => (
        <Checkbox
          checked={record.inserir}
          onChange={(e) => updatePermissao(record.menu.id, 'inserir', e.target.checked)}
        />
      ),
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Perfil" : "Cadastro de Perfil"}</span>
        </div>
        
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="col-span-2">
            <label>Nome *</label>
            <Input 
              type="text" 
              placeholder="Nome do perfil" 
              {...register("nome", { required: "Nome é obrigatório" })}
              onChange={(e) => setValue("nome", e.target.value.toUpperCase(), { shouldValidate: true })}
              value={watch("nome")}
            />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          
          <div className="flex items-center mt-6">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Checkbox
                id="ativo"
                checked={watch("ativo")}
                onChange={(e) => setValue("ativo", e.target.checked, { shouldValidate: true })}
              >
                <label htmlFor="ativo" className="text-white ml-2">Ativo</label>
              </Checkbox>
            </ConfigProvider>
          </div>
        </div>

        <div className="mb-6">
          <label className="text-white text-lg font-semibold">Permissões</label>
        </div>

        <div className="mb-8">
          <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
            <Table 
              columns={columns} 
              dataSource={permissoes} 
              rowKey={(record) => record.menu.id}
              pagination={false}
              size="small"
            />
          </ConfigProvider>
        </div>

        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/perfil")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
} 